/**
 * GTM tracking utilities
 */

import {
    CUSTOM_EVENTS,
    GTM_EVENT_NAMES,
    CART_ACTIONS,
    ITEM_LIST_NAMES,
    COMPONENT_TIMEOUTS,
} from '../constants/product.constants';
import { ProductSelectedInfo, ProductConfiguration } from '../types/product.types';
import { ISwatchConfig } from 'components/Catalog/Product/Detail/Configurator';

interface ProductTrackingData {
    name: string;
    id: string;
    fullprice: number;
    discount: number;
    brand?: string;
    category?: string;
}

interface CartTrackingData {
    action: string;
    userId?: string;
    itemListName: string;
    sku: string;
    name: string;
    priceValue: number;
    itemBrand?: string;
}

interface ProductConfigurationData {
    productConfig: ProductConfiguration;
    selectedProductInformation: ProductSelectedInfo;
    swatchConfig?: ISwatchConfig;
}

/**
 * Tracks product view event for GTM
 * @param productData - Product information for tracking
 * @param userId - User ID if logged in
 */
export const trackProductView = (productData: ProductTrackingData, userId?: string): void => {
    window.dispatchEvent(
        new CustomEvent(CUSTOM_EVENTS.GTM_PRODUCT_EVENT, {
            detail: {
                event_name: GTM_EVENT_NAMES.VIEW_ITEM,
                user_id: userId,
                ...productData,
            },
        }),
    );
};

/**
 * Tracks add to cart event for GTM
 * @param cartData - Cart tracking data
 */
export const trackAddToCart = (cartData: CartTrackingData): void => {
    window.dispatchEvent(
        new CustomEvent(CUSTOM_EVENTS.CART_ALTERED, {
            detail: {
                action: CART_ACTIONS.ADD_TO_CART,
                itemListName: ITEM_LIST_NAMES.PRODUCT_DETAIL_VIEW,
                ...cartData,
            },
        }),
    );
};

/**
 * Tracks product configuration change event
 * @param configData - Product configuration data
 */
export const trackProductConfigurationChange = (configData: ProductConfigurationData): void => {
    // Add timeout to allow component rendering
    setTimeout(() => {
        window.dispatchEvent(
            new CustomEvent(CUSTOM_EVENTS.PRODUCT_CONFIGURATION_CHANGE, {
                detail: configData,
            }),
        );
    }, COMPONENT_TIMEOUTS.PRODUCT_CONFIGURATION_CHANGE);
};

/**
 * Prepares product tracking data from product config
 * @param productConfig - Product configuration object
 * @param priceOld - Old price if available
 * @returns Formatted tracking data
 */
export const prepareProductTrackingData = (
    productConfig: ProductConfiguration,
    priceOld?: string,
): ProductTrackingData => {
    const productPrice = productConfig.price ? productConfig.price.value : 0;

    return {
        name: productConfig.name,
        id: productConfig.sku.value,
        fullprice: priceOld ? parseFloat(priceOld) : productPrice,
        discount: priceOld ? productPrice : 0,
        brand: productConfig.itemBrand,
        category: productConfig?.category,
    };
};

/**
 * Prepares cart tracking data
 * @param productConfig - Product configuration
 * @param selectedProductInformation - Selected product info
 * @param userId - User ID if available
 * @returns Formatted cart tracking data
 */
export const prepareCartTrackingData = (
    productConfig: ProductConfiguration,
    selectedProductInformation: ProductSelectedInfo,
    userId?: string,
): CartTrackingData => {
    return {
        action: CART_ACTIONS.ADD_TO_CART,
        userId,
        itemListName: ITEM_LIST_NAMES.PRODUCT_DETAIL_VIEW,
        sku: selectedProductInformation?.sku || productConfig?.sku?.value,
        name: productConfig?.name,
        priceValue: productConfig?.price?.value,
        itemBrand: productConfig?.itemBrand,
    };
};
